// Dizionario prima degli altri moduli locali: si auto-registra su i18n.js.
import { t, PAGE_LANG, langPath } from './i18n.js';
import './cvBuilderI18n.en.js';
import JSZip from 'jszip';
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs';
import { createPdfImportStepper } from './pdfImportStepper.js';
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('./pdf.worker.mjs', import.meta.url).toString();

const LEGACY_STORAGE_KEY = 'cv_builder_draft_v2';
const STORAGE_KEY = 'cv_builder_workspace_v3';
const PENDING_ZIP_STORAGE_KEY = 'cv_builder_pending_import_zip';
const WORKSPACE_SCHEMA_VERSION = 3;
const STORAGE_DB_NAME = 'cv_builder_db';
const STORAGE_STORE_NAME = 'drafts';
const editorUrl = langPath('/cv-builder/editor/');

const startButton = document.querySelector('#cv-start');
const resumeButton = document.querySelector('#cv-resume');
const importButton = document.querySelector('#cv-import');
const importInput = document.querySelector('#cv-import-file');
// Tutti i pulsanti «inizia» della pagina, non solo il primo: con querySelector
// quelli dopo il primo (la chiamata finale in fondo alla landing) non facevano
// niente.
const ctaStarts = Array.from(document.querySelectorAll('[data-action="cta-start"]'));
const previewTemplateButtons = Array.from(document.querySelectorAll('.cv-preview-cta'));
const ctaImport = document.querySelector('[data-action="cta-import"]');
const ctaResume = document.querySelector('[data-action="cta-resume"]');
const importPdfBtn = document.querySelector('#cv-import-pdf-btn');
const importPdfInput = document.querySelector('#cv-import-pdf-file');
const ctaImportPdf = document.querySelector('[data-action="cta-import-pdf"]');
const downloadCountEls = Array.from(document.querySelectorAll('[data-download-count]'));
const downloadMetricEls = Array.from(document.querySelectorAll('[data-downloads-section]'));
const downloadsSections = Array.from(document.querySelectorAll('[data-downloads-section]'));

function getCvBuilderDownloadStatsDocId(baseUrl) {
  const normalizedBaseUrl = String(baseUrl || '').trim();
  if (!normalizedBaseUrl) return '';
  return `cvBuilderPdfDownloads_${encodeURIComponent(normalizedBaseUrl)}`;
}

function hideDownloadMetrics() {
  downloadMetricEls.forEach((el) => {
    el.hidden = true;
  });
}

function setDownloadMetricsBusy(isBusy) {
  downloadMetricEls.forEach((el) => {
    el.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  });
}

// IndexedDB helpers
let dbInstance = null;

async function openDatabase() {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }
    const request = indexedDB.open(STORAGE_DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORAGE_STORE_NAME)) {
        db.createObjectStore(STORAGE_STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

async function loadFromIndexedDB(key) {
  try {
    const db = await openDatabase();
    return new Promise((resolve) => {
      const transaction = db.transaction([STORAGE_STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORAGE_STORE_NAME);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function saveToIndexedDB(key, data) {
  try {
    const db = await openDatabase();
    return new Promise((resolve) => {
      const transaction = db.transaction([STORAGE_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORAGE_STORE_NAME);
      store.put({ id: key, data, updatedAt: Date.now() });
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
}

async function deleteFromIndexedDB(key) {
  try {
    const db = await openDatabase();
    return new Promise((resolve) => {
      const transaction = db.transaction([STORAGE_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORAGE_STORE_NAME);
      store.delete(key);
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
}

function createCvId(prefix = 'cv') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function inferCvName(cv, fallback = t('CV principale')) {
  const fullName = typeof cv?.profile?.fullName === 'string' ? cv.profile.fullName.trim() : '';
  return fullName ? `CV ${fullName}` : fallback;
}

function isStoredWorkspace(data) {
  return Boolean(
    data
    && typeof data === 'object'
    && Array.isArray(data.cvs)
    && data.cvs.length > 0
  );
}

function wrapSingleCvAsWorkspace(cv, { name = '', source = 'legacy-json' } = {}) {
  const id = createCvId();
  const now = new Date().toISOString();
  return {
    schemaVersion: WORKSPACE_SCHEMA_VERSION,
    activeCvId: id,
    cvs: [{
      id,
      name: name || inferCvName(cv, t('CV importato')),
      createdAt: now,
      updatedAt: now,
      source,
      cv,
      coachTipsDelivered: 0,
      coachActiveTipId: '',
      coachViewedTipIds: []
    }]
  };
}

function setButtonLabel(button, label) {
  if (!button) return;
  const labelEl = button.querySelector('.button-text');
  if (labelEl) {
    labelEl.textContent = label;
    return;
  }
  button.textContent = label;
}

async function hasDraft() {
  const workspace = await loadFromIndexedDB(STORAGE_KEY);
  if (isStoredWorkspace(workspace)) return true;

  const legacy = await loadFromIndexedDB(LEGACY_STORAGE_KEY);
  if (legacy && typeof legacy === 'object') return true;

  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object';
  } catch {
    return false;
  }
}

function goToEditor() {
  window.location.href = editorUrl;
}

function openImportDialog() {
  if (importInput) importInput.click();
}

async function applyDraftState() {
  const draftAvailable = await hasDraft();
  if (draftAvailable) {
    if (resumeButton) resumeButton.classList.remove('is-hidden');
    if (startButton) {
      setButtonLabel(startButton, t('Parti da zero'));
      startButton.dataset.reset = 'true';
    }
    if (resumeButton) {
      setButtonLabel(resumeButton, t('Continua da dove avevi lasciato'));
    }
    if (importButton) {
      setButtonLabel(importButton, t('Importa backup'));
    }
    if (ctaResume) {
      ctaResume.classList.remove('is-hidden');
      setButtonLabel(ctaResume, t('Continua da dove avevi lasciato'));
    }
    if (ctaImport) {
      setButtonLabel(ctaImport, t('Importa backup'));
    }
  }
}

async function clearStoredWorkspace() {
  await deleteFromIndexedDB(STORAGE_KEY);
  await deleteFromIndexedDB(PENDING_ZIP_STORAGE_KEY);
  await deleteFromIndexedDB(LEGACY_STORAGE_KEY);
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore
  }
}

async function handleStartClick() {
  if (startButton?.dataset.reset === 'true') {
    try {
      await clearStoredWorkspace();
    } catch (error) {
      console.warn('Impossibile rimuovere il workspace salvato', error);
    }
  }
  goToEditor();
}

async function handleImportFile(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  try {
    if (/\.zip$/i.test(file.name) || file.type.includes('zip')) {
      const zip = await JSZip.loadAsync(file);
      const workspaceEntry = zip.file('workspace.json');
      if (!workspaceEntry) throw new Error('workspace.json mancante');
      const workspace = JSON.parse(await workspaceEntry.async('string'));
      if (!isStoredWorkspace(workspace)) throw new Error('Formato workspace non valido');
      await saveToIndexedDB(STORAGE_KEY, workspace);
      await saveToIndexedDB(PENDING_ZIP_STORAGE_KEY, await file.arrayBuffer());
    } else {
      const parsed = JSON.parse(await file.text());
      if (!parsed || typeof parsed !== 'object') throw new Error('Formato non valido');
      const workspace = isStoredWorkspace(parsed)
        ? parsed
        : wrapSingleCvAsWorkspace(parsed);
      await saveToIndexedDB(STORAGE_KEY, workspace);
    }
    goToEditor();
  } catch (error) {
    console.error('Import fallito', error);
    alert(t('Il file selezionato non è un backup valido.'));
  } finally {
    event.target.value = '';
  }
}

// Initialize
applyDraftState();

startButton?.addEventListener('click', handleStartClick);
resumeButton?.addEventListener('click', goToEditor);
importButton?.addEventListener('click', openImportDialog);
// Solo «Parti da zero» (#cv-start) svuota il workspace salvato, e lo dice
// nell'etichetta. Le altre chiamate all'azione passavano dallo stesso gestore: a
// chi tornava con dei CV salvati, un clic su «Prova gratis» o «Create your CV» li
// cancellava tutti senza chiedere, e senza Premium erano solo sul dispositivo.
// Ora aprono l'editor, dove il workspace c'e' gia' e un CV nuovo si aggiunge
// agli altri.
ctaStarts.forEach((button) => button.addEventListener('click', goToEditor));
previewTemplateButtons.forEach((button) => button.addEventListener('click', goToEditor));
ctaImport?.addEventListener('click', openImportDialog);
ctaResume?.addEventListener('click', goToEditor);
importInput?.addEventListener('change', handleImportFile);

// --- PDF Import from Landing ---
const PARSE_CV_TIMEOUT_MS = 45000;

function showLandingPdfConfirmDialog() {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:80;';
    overlay.innerHTML = `
      <div style="position:absolute;inset:0;background:rgba(15,23,42,0.5);" data-action="cancel"></div>
      <div style="position:relative;background:#fff;border-radius:14px;padding:1.5rem;width:min(520px,92vw);box-shadow:0 10px 40px rgba(0,0,0,0.15);z-index:1;display:flex;flex-direction:column;gap:1rem;">
        <h3 style="margin:0;font-size:1.1rem;">${t('Importa da PDF')}</h3>
        <p style="margin:0;font-size:0.95rem;color:#475569;">${t("Per estrarre le informazioni dal tuo CV, il testo verrà inviato a Google (Gemini) per l'analisi. Nessun dato verrà memorizzato sui sistemi di Stipendee.")}</p>
        <div style="display:flex;flex-direction:column;gap:0.6rem;">
          <button type="button" class="myButton" data-action="confirm">${t('Continua')}</button>
          <button type="button" class="myButton white-button" data-action="cancel">${t('Annulla')}</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    function cleanup(result) {
      overlay.remove();
      resolve(result);
    }

    overlay.querySelectorAll('[data-action="confirm"]').forEach((el) => el.addEventListener('click', () => cleanup(true)));
    overlay.querySelectorAll('[data-action="cancel"]').forEach((el) => el.addEventListener('click', () => cleanup(false)));
  });
}

function withTimeout(promise, timeoutMs, timeoutMessage) {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId);
  });
}

function openPdfImportDialog() {
  if (importPdfInput) importPdfInput.click();
}

async function handleImportPdfFromLanding(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  const confirmed = await showLandingPdfConfirmDialog();
  if (!confirmed) return;

  const stepper = createPdfImportStepper();
  stepper.open();

  try {
    // Extract text from PDF
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const pages = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      pages.push(content.items.map((item) => item.str).join(' '));
    }
    const pdfText = pages.join('\n').trim();

    if (!pdfText || pdfText.length < 20) {
      stepper.fail(t('Non è stato possibile leggere il testo dal PDF. Prova con un PDF diverso.'));
      return;
    }

    // Get Firebase functions instance
    const initFn = window.firebaseInit;
    if (typeof initFn !== 'function') {
      stepper.fail(t('Qualcosa è andato storto. Riprova.'));
      return;
    }
    const { app } = initFn();
    const functions = getFunctions(app);
    const parseCvFromText = httpsCallable(functions, 'parseCvFromText');

    const result = await withTimeout(
      parseCvFromText({ text: pdfText, uiLang: PAGE_LANG }),
      PARSE_CV_TIMEOUT_MS,
      'timeout'
    );
    const parsed = result?.data;
    if (!parsed || typeof parsed !== 'object') {
      stepper.fail(t('Qualcosa è andato storto. Riprova.'));
      return;
    }

    // parseCvFromText non restituisce un locale: dalla landing EN i CV importati nascono en-GB.
    if (PAGE_LANG === 'en' && !parsed.locale) parsed.locale = 'en-GB';

    const workspace = wrapSingleCvAsWorkspace(parsed, {
      name: inferCvName(parsed, t('CV da PDF')),
      source: 'pdf-import'
    });
    await saveToIndexedDB(STORAGE_KEY, workspace);
    stepper.complete();
    window.setTimeout(() => goToEditor(), 900);
  } catch (error) {
    console.error('Import PDF fallito', error);
    const rawCode = typeof error?.code === 'string' ? error.code : '';
    const errorCode = rawCode.startsWith('functions/') ? rawCode.slice('functions/'.length) : rawCode;
    const errorMsg = error?.message || '';
    if (errorMsg === 'timeout' || errorCode === 'deadline-exceeded') {
      stepper.fail(t('La richiesta ha impiegato troppo tempo. Riprova.'));
    } else if (errorCode === 'resource-exhausted') {
      stepper.fail(t('Troppe richieste. Riprova tra qualche minuto.'));
    } else {
      stepper.fail(t('Qualcosa è andato storto. Riprova.'));
    }
  }
}

importPdfBtn?.addEventListener('click', openPdfImportDialog);
ctaImportPdf?.addEventListener('click', openPdfImportDialog);
importPdfInput?.addEventListener('change', handleImportPdfFromLanding);

function initHowCarousels() {
  const carousels = document.querySelectorAll('[data-how-carousel]');

  carousels.forEach((carousel) => {
    const track = carousel.querySelector('[data-how-carousel-track]');
    const slides = Array.from(carousel.querySelectorAll('[data-how-carousel-slide]')).filter((slide) => slide instanceof HTMLElement);
    const prevButton = carousel.querySelector('[data-how-carousel-prev]');
    const nextButton = carousel.querySelector('[data-how-carousel-next]');
    const dotsContainer = carousel.parentElement?.querySelector('[data-how-carousel-dots]');
    if (!(track instanceof HTMLElement) || !slides.length) return;

    const dots = dotsContainer
      ? slides.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cv-how-carousel-dot';
        dot.setAttribute('aria-label', t('Vai allo screenshot {n}', { n: index + 1 }));
        dot.addEventListener('click', () => scrollToSlide(index));
        dotsContainer.appendChild(dot);
        return dot;
      })
      : [];

    function getCurrentIndex() {
      return slides.reduce((closestIndex, slide, index) => {
        const currentDistance = Math.abs(slide.offsetLeft - track.scrollLeft);
        const closestDistance = Math.abs(slides[closestIndex].offsetLeft - track.scrollLeft);
        return currentDistance < closestDistance ? index : closestIndex;
      }, 0);
    }

    function updateViewportHeight() {
      const slide = slides[getCurrentIndex()];
      if (!slide) return;
      track.style.height = `${slide.offsetHeight}px`;
    }

    // Cambiare l'altezza del track a metà swipe causa un reflow che interrompe lo
    // scroll snap (si resta fermi tra due slide): l'altezza va aggiornata solo quando
    // lo scroll si è assestato, cioè ~150ms dopo l'ultimo evento di scroll.
    let heightSettleTimer = null;
    function scheduleViewportHeightUpdate() {
      window.clearTimeout(heightSettleTimer);
      heightSettleTimer = window.setTimeout(updateViewportHeight, 150);
    }

    function scrollToSlide(index) {
      const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
      track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }

    let lastActiveIndex = -1;

    function updateControls() {
      const activeIndex = getCurrentIndex();
      if (prevButton instanceof HTMLButtonElement) prevButton.disabled = activeIndex === 0;
      if (nextButton instanceof HTMLButtonElement) nextButton.disabled = activeIndex === slides.length - 1;
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
      if (activeIndex !== lastActiveIndex) {
        lastActiveIndex = activeIndex;
      }
    }

    let frameId = null;
    function requestControlsUpdate() {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateControls);
    }

    prevButton?.addEventListener('click', () => scrollToSlide(getCurrentIndex() - 1));
    nextButton?.addEventListener('click', () => scrollToSlide(getCurrentIndex() + 1));
    track.addEventListener('scroll', () => {
      requestControlsUpdate();
      scheduleViewportHeightUpdate();
    }, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollToSlide(getCurrentIndex() - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollToSlide(getCurrentIndex() + 1);
      }
    });
    window.addEventListener('resize', () => {
      requestControlsUpdate();
      scheduleViewportHeightUpdate();
    });

    slides.forEach((slide) => {
      slide.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', scheduleViewportHeightUpdate, { once: true });
      });
    });

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => scheduleViewportHeightUpdate());
      slides.forEach((slide) => resizeObserver.observe(slide));
    }

    updateControls();
    updateViewportHeight();
  });
}

initHowCarousels();

// Il carosello dell'hero: le anteprime dei modelli in dissolvenza, senza
// scritte ne' comandi. Avanza da solo ogni INTERVALLO, ma si ferma col mouse
// sopra, quando non si vede (fuori schermo, scheda in secondo piano, o la copia
// per telefono su desktop) e per chi chiede meno movimento, che vede la prima
// anteprima ferma. La prima anteprima e' nell'HTML, le altre sono nascoste e
// si caricano una alla volta prima di comparire.
function initHeroCaroselli() {
  const INTERVALLO = 3500;
  const menoMovimento = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.querySelectorAll('[data-hero-carosello]').forEach((carosello) => {
    const foto = Array.from(carosello.querySelectorAll('.cv-hero-carosello-foto'));
    if (foto.length < 2) return;
    let attiva = 0;
    let timer = null;
    let sopra = false;
    let visibile = false;

    const prepara = (indice) => {
      const img = foto[indice];
      img.hidden = false;
      return img.decode().catch(() => {});
    };
    const mostra = async (indice) => {
      await prepara(indice);
      foto.forEach((img, n) => {
        img.classList.toggle('is-attiva', n === indice);
        img.setAttribute('aria-hidden', n === indice ? 'false' : 'true');
      });
      attiva = indice;
      // La prossima si carica mentre questa resta a schermo.
      prepara((indice + 1) % foto.length);
    };
    const ferma = () => {
      clearTimeout(timer);
      timer = null;
    };
    const programma = () => {
      ferma();
      if (menoMovimento.matches || sopra || !visibile || document.hidden) return;
      timer = setTimeout(async () => {
        await mostra((attiva + 1) % foto.length);
        programma();
      }, INTERVALLO);
    };

    carosello.addEventListener('mouseenter', () => { sopra = true; ferma(); });
    carosello.addEventListener('mouseleave', () => { sopra = false; programma(); });
    document.addEventListener('visibilitychange', programma);
    menoMovimento.addEventListener?.('change', programma);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([voce]) => {
        visibile = voce.isIntersecting;
        programma();
      }, { threshold: 0.3 }).observe(carosello);
    } else {
      visibile = true;
      programma();
    }
  });
}

initHeroCaroselli();

const zoomModal = document.getElementById('cv-zoom-modal');
const zoomImage = zoomModal?.querySelector('.cv-zoom-image');
const zoomTriggers = document.querySelectorAll('[data-zoom-trigger]');
let lastZoomFocus = null;

function openZoomModal(trigger) {
  if (!zoomModal || !zoomImage) return;
  const img = trigger.querySelector('img');
  if (!img) return;
  zoomImage.src = img.currentSrc || img.src;
  zoomImage.alt = img.alt || t('Anteprima CV');
  zoomModal.classList.add('is-open');
  zoomModal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('cv-zoom-open');
  lastZoomFocus = document.activeElement;
  const closeButton = zoomModal.querySelector('[data-zoom-action="close"]');
  if (closeButton instanceof HTMLElement) closeButton.focus();
}

function closeZoomModal() {
  if (!zoomModal || !zoomImage) return;
  zoomModal.classList.remove('is-open');
  zoomModal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('cv-zoom-open');
  zoomImage.src = '';
  zoomImage.alt = '';
  if (lastZoomFocus instanceof HTMLElement) {
    lastZoomFocus.focus();
  }
  lastZoomFocus = null;
}

const isMobileZoomDisabled = () => window.matchMedia('(max-width: 768px)').matches;

zoomTriggers.forEach((trigger) => {
  trigger.addEventListener('click', () => {
    if (isMobileZoomDisabled()) return;
    openZoomModal(trigger);
  });
});

function formatDownloadCount(value) {
  const abs = Math.abs(value);
  if (abs >= 1000000) {
    return `${trimDecimal(value / 1000000)}M`;
  }
  if (abs >= 1000) {
    return `${trimDecimal(value / 1000)}k`;
  }
  return Math.round(value).toString();
}

function trimDecimal(value) {
  const rounded = Math.round(value * 10) / 10;
  const text = rounded.toFixed(1);
  return text.endsWith('.0') ? text.slice(0, -2) : text;
}

function animateDownloadCount(target) {
  if (!downloadCountEls.length) return;
  if (target <= 0) {
    hideDownloadMetrics();
    return;
  }
  const duration = 1400;
  const startTime = performance.now();
  const startValue = 0;

  function tick(now) {
    const progress = Math.min((now - startTime) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = Math.round(startValue + (target - startValue) * eased);
    const formatted = formatDownloadCount(current);
    downloadCountEls.forEach((el) => {
      el.textContent = formatted;
    });
    if (progress < 1) {
      requestAnimationFrame(tick);
    }
  }

  requestAnimationFrame(tick);
}

async function loadDownloadCount() {
  if (!downloadCountEls.length) return 0;
  setDownloadMetricsBusy(true);
  try {
    const initFn = window.firebaseInit;
    if (typeof initFn !== 'function') {
      throw new Error('firebaseInit non disponibile');
    }
    const { app } = initFn();
    const db = getFirestore(app);
    const statsDocId = getCvBuilderDownloadStatsDocId(window.location.origin);
    if (!statsDocId) throw new Error('Base URL non disponibile');
    const snapshot = await getDoc(doc(db, 'publicStats', statsDocId));
    const total = Number(snapshot.data()?.total || 0);
    setDownloadMetricsBusy(false);
    if (total <= 0) {
      hideDownloadMetrics();
      return 0;
    }
    downloadCountEls.forEach((el) => {
      el.dataset.target = String(total);
    });
    return total;
  } catch (error) {
    console.warn('Conteggio download CV non disponibile', error);
    setDownloadMetricsBusy(false);
    hideDownloadMetrics();
    return 0;
  }
}

async function initDownloadCount() {
  if (!downloadCountEls.length || !downloadsSections.length) return;
  const total = await loadDownloadCount();
  if (total <= 0) return;

  let hasAnimated = false;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || hasAnimated) return;
      hasAnimated = true;
      animateDownloadCount(total);
      observer.disconnect();
    });
  }, { threshold: 0.35 });

  downloadsSections.forEach((section) => observer.observe(section));
}

initDownloadCount();

zoomModal?.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (target.closest('[data-zoom-action="close"]')) {
    closeZoomModal();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  if (!zoomModal?.classList.contains('is-open')) return;
  closeZoomModal();
});

// Evento GTM al primo play del video demo (il tag lato GTM lo configura il dashboard).
document.querySelectorAll('.cv-video-player').forEach((video) => {
  video.addEventListener('play', () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cv_video_play' });
  }, { once: true });
});
